import { useMemo } from 'react';
import { useTable, useSortBy } from 'react-table';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { useQuery } from '@apollo/client';

import ThemeLink from '../../components/ThemeLink';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorText from '../../components/ErrorText';
import { formatDateCellShort } from '../../helpers/reactTable';
import { ASK_ADDY_CONVERSATIONS_QUERY } from '../../queries/askAddy';
import { capitalize } from '../../utils';

// Friendly label per prompt type (mirrors the action titles in AskAddy.tsx).
const PROMPT_TYPE_LABELS: Record<string, string> = {
  exec_summary: 'Executive summary',
  talking_points: 'Top talking points',
  client_email: 'Sales email',
  audience_recs: 'Audience recommendations',
  cog_sci_playbook: 'Cognitive science playbook',
};

function AskAddyChatsTable({ data }: { data: any[] }) {
  const columns = useMemo(
    () => [
      {
        Header: 'Profile Report',
        id: 'reportName',
        accessor: 'reportName',
        Cell: ({ value }: { value: string }) => value || 'Profile report',
      },
      {
        Header: 'Request',
        accessor: 'promptType',
        Cell: ({ value }: { value: string }) =>
          PROMPT_TYPE_LABELS[value] || value,
      },
      {
        Header: 'Last Updated',
        accessor: 'updatedAt',
        Cell: ({ value }: { value: string }) => formatDateCellShort({ value }),
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }: { value: string }) => capitalize(value),
      },
      {
        Header: 'Actions',
        id: 'actions',
        // Re-open and restore the conversation in Ask Addy. The `prompt` param
        // auto-starts the prompt, which reuses the existing ready conversation
        // (no new model charge) and restores its output + chat history.
        Cell: ({ row }: { row: any }) => (
          <ThemeLink
            to={`../../ask-addy/${row.original.packageId}?prompt=${row.original.promptType}`}
          >
            Open
          </ThemeLink>
        ),
      },
    ],
    []
  );

  const initialSortBy = useMemo(() => [{ id: 'updatedAt', desc: true }], []);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { sortBy: initialSortBy },
    },
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = tableInstance;

  return (
    <div className="h-full w-full overflow-auto pb-52">
      <table {...getTableProps()} className="w-full">
        <thead className="whitespace-nowrap">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className="border-b-2 border-black p-2 hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    {column.render('Header')}
                    {column.isSorted && (
                      <span className="pl-2">
                        {column.isSortedDesc ? <FaArrowDown /> : <FaArrowUp />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="whitespace-nowrap">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} key={row.original.id}>
                {row.cells.map((cell) => (
                  <td className="p-2" {...cell.getCellProps()}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="text-center pt-6">
          You haven't asked Addy anything yet. Pick a profile report and an
          action in{' '}
          <ThemeLink to="../../ask-addy">Ask Addy.</ThemeLink>
        </p>
      )}
    </div>
  );
}

export default function AskAddyChats() {
  const { loading, error, data } = useQuery(ASK_ADDY_CONVERSATIONS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  return (
    <div className="w-full">
      {loading && (
        <LoadingSpinner message="Getting your Ask Addy requests..." className="mt-10" />
      )}
      {!loading && error && <ErrorText message={error.message} />}
      {!loading && !error && data && (
        <AskAddyChatsTable data={data.askAddyConversations} />
      )}
    </div>
  );
}
